import type { BlogPost } from '../../types/blog';

export const post: BlogPost = {
  category: 'guias',
  author: 'Ivan Henrique',
  publishedAt: '2026-05-05',
  coverImage: '/blog/copa-do-brasil-2026.svg',
  tags: ['Copa do Brasil', 'Mata-mata', 'CBF', 'Bolão'],
  i18n: {
    'pt-BR': {
      slug: 'copa-do-brasil-2026',
      title: 'Copa do Brasil 2026: como funciona o mata-mata e o impacto no bolão',
      excerpt:
        'Torneio com 92 participantes, jogos eliminatórios e premiação milionária — entenda o formato da Copa do Brasil e por que ela exige uma estratégia de palpites diferente.',
      readTimeMinutes: 7,
      coverImageAlt: 'Capa do artigo: Copa do Brasil 2026 — o mata-mata explicado',
      keywords: ['copa do brasil 2026', 'mata-mata', 'eliminatórias', 'bolão copa do brasil', 'agregado'],
      content: (
        <>
          <p>
            A Copa do Brasil é uma das competições mais democráticas do calendário:
            começa com cerca de 92 times de todas as Séries (A, B, C e D) e termina
            com apenas um campeão, depois de uma série brutal de mata-matas. O
            prêmio é generoso, a tradição é antiga (existe desde 1989) e a
            possibilidade de zebra é altíssima. Isso tudo faz dela uma das mais
            emocionantes — e também das mais difíceis de palpitar.
          </p>
          <p>
            Vamos explicar como funciona o torneio e, mais importante, como ele
            afeta a sua estratégia no bolão.
          </p>

          <h2>O formato eliminatório (mata-mata puro)</h2>
          <p>
            Ao contrário do Brasileirão, na Copa do Brasil <strong>não existe
            ponto corrido</strong>. Tudo é eliminatório. Você perdeu? Saiu. Não há
            segunda chance.
          </p>
          <p>
            A Copa do Brasil se divide nas seguintes fases (formato típico —
            variações pontuais podem ocorrer conforme regulamento anual):
          </p>
          <ul>
            <li><strong>1ª e 2ª fases:</strong> jogo único, na casa do time pior ranqueado</li>
            <li><strong>3ª fase:</strong> jogo de ida e volta</li>
            <li><strong>Oitavas, quartas e semifinais:</strong> ida e volta</li>
            <li><strong>Final:</strong> ida e volta</li>
          </ul>
          <p>
            Nas duas primeiras fases, jogo único significa que <strong>quem joga
            em casa precisa apenas vencer</strong> — empate em 90 minutos elimina
            o mandante. Nessas fases entram principalmente times da Série C e D,
            com poucas estrelas de elite no campo. As surpresas começam aqui.
          </p>

          <h2>Quando entra cada time?</h2>
          <p>
            A Copa do Brasil tem entradas escalonadas para garantir que os times
            grandes não eliminem times pequenos logo de cara:
          </p>
          <ul>
            <li>
              Times de Série C, D e clubes campeões estaduais menores entram nas
              1ª e 2ª fases.
            </li>
            <li>
              Times de Série B entram a partir da 3ª fase.
            </li>
            <li>
              <strong>Times de Série A entram só nas oitavas de final</strong> (com
              poucas exceções, como times rebaixados recentemente).
            </li>
          </ul>
          <p>
            Isso significa que, para um time da Série A, a Copa do Brasil
            &quot;começa de verdade&quot; nas oitavas. Mas pra todo torcedor de
            time pequeno, é uma chance de ouro: cada vitória contra um time
            maior vale milhões em premiação e prestígio.
          </p>

          <h2>Premiação: por que todo time leva a sério</h2>
          <p>
            A Copa do Brasil é, de longe, a competição com a melhor premiação
            proporcional do país. Cada fase elimina dos clubes a chance de
            receber valores adicionais, que somados podem passar de R$ 80 milhões
            para o campeão. Para clubes do interior ou da Série C, avançar 2 ou 3
            fases pode equivaler ao orçamento anual completo.
          </p>
          <p>
            Tradução pro bolão: <strong>todos os times jogam pra valer</strong>.
            Não tem &quot;time reserva pra preservar pra outra coisa&quot; — pra
            clubes pequenos, a Copa do Brasil É a competição principal.
          </p>

          <h2>Por que a Copa do Brasil é tão difícil de palpitar</h2>
          <p>
            Três razões principais:
          </p>

          <h3>1. Conhecimento desigual entre os participantes</h3>
          <p>
            Você provavelmente sabe escalação do Flamengo, Palmeiras e Atlético-MG.
            Mas e o Boavista-RJ? E o ABC? E o Sousa-PB? <strong>Sem informação,
            seus palpites em jogos das fases iniciais são chute</strong>. Quem
            acompanha futebol regional ou Série C/D tem vantagem real aqui.
          </p>

          <h3>2. Jogo único favorece zebra</h3>
          <p>
            Em mata-mata de jogo único, basta um time errar uma jogada e o azarão
            leva. Em ida e volta, o time melhor tem 180 minutos para se impor; em
            jogo único, são só 90. Estatisticamente, <strong>jogo único da Copa
            do Brasil tem cerca de 30% mais zebras que ida e volta</strong>.
          </p>

          <h3>3. Critério de desempate confuso para muita gente</h3>
          <p>
            Em ida e volta, o critério de desempate é o agregado (soma dos
            placares dos dois jogos). Se o agregado for igual, vai para
            prorrogação e, persistindo o empate, para pênaltis. <strong>Não
            existe mais &quot;gol fora de casa vale dobrado&quot;</strong> na
            Copa do Brasil desde 2022. Quem palpita pensando na regra antiga
            erra muito.
          </p>

          <h2>Estratégia de bolão na Copa do Brasil</h2>
          <p>
            Considerando essas características, três recomendações:
          </p>

          <h3>1. Seja conservador em fase de grupos jogo único</h3>
          <p>
            Quando dois times muito desiguais se enfrentam (Série A vs Série C),
            palpite na vitória do favorito, mas evite goleadas. <strong>Nas
            primeiras fases, jogos costumam ser truncados</strong> — o time
            grande não impõe ritmo total porque tá com a cabeça em outras
            competições, e o pequeno se fecha. 1x0 e 2x0 são placares muito mais
            comuns que 4x0 ou 5x0 nessas fases.
          </p>

          <h3>2. Aposte em jogo de volta para acertar agregado</h3>
          <p>
            Em ida e volta, o jogo de volta costuma ser mais decisivo. Se o
            primeiro jogo foi 0x0 ou 1x1, espere um jogo mais aberto no segundo
            — o time da casa precisa abrir o placar pra ter vantagem. <strong>Jogos
            de volta empolgam mais e têm placares maiores</strong>.
          </p>

          <h3>3. Atenção ao calendário interno dos times</h3>
          <p>
            Times de Série A que disputam Libertadores costumam priorizar o
            torneio continental. Se um clube grande tem jogo na Libertadores 3
            dias depois, é provável que escale time reserva na Copa do Brasil. Aí
            vale apostar em zebra — mas com cuidado, porque essa &quot;escolha&quot;
            nem sempre acontece.
          </p>

          <h2>Como criar um bolão da Copa do Brasil no Bolão Elite</h2>
          <p>
            A Copa do Brasil tem fases muito assimétricas — algumas com poucos
            jogos, outras com várias. No Bolão Elite, a página{' '}
            <a href="/bolao-copa-do-brasil">bolão da Copa do Brasil</a> já vem
            com a estrutura de fases configurada. Você cria o bolão, escolhe a
            pontuação e o sistema importa as partidas automaticamente.
          </p>
          <p>
            Recomendamos a Regra C (10/5/2) — placar exato vale 10 pts,
            vencedor/empate 5 pts, gols de um dos times corretos 2 pts.
            Como Copa do Brasil tem muitos jogos truncados, a granularidade
            ajuda a manter todo mundo na briga até o fim. Quem prefere
            simplicidade pode usar a Regra B (3/1).
          </p>

          <h2>Conclusão</h2>
          <p>
            Copa do Brasil é uma loteria controlada: tem favoritos claros (os
            times de Série A), mas o caminho até a final passa por muitos
            jogos onde a zebra tem força. Pra fazer um bolão dela, prepare-se
            para errar mais que de costume — e divirta-se com isso. As
            surpresas são o que tornam essa competição única no calendário
            brasileiro.
          </p>
          <p>
            Para mais conteúdo,{' '}
            <a href="/blog">veja todos os artigos do blog</a>. Para entender
            termos como &quot;agregado&quot; e &quot;mata-mata&quot;, dê uma
            olhada no <a href="/glossario">glossário</a>.
          </p>
        </>
      ),
    },
    en: {
      slug: 'copa-do-brasil-2026',
      title: 'Copa do Brasil 2026: how the knockout stage works and its impact on the pool',
      excerpt:
        'A tournament with 92 entrants, knockout matches and millions in prize money — understand the Copa do Brasil format and why it demands a different prediction strategy.',
      readTimeMinutes: 7,
      coverImageAlt: 'Article cover: Copa do Brasil 2026 — the knockout stage explained',
      coverImage: '/blog/copa-do-brasil-2026-en.svg',
      keywords: ['copa do brasil 2026', 'knockout stage', 'knockout ties', 'copa do brasil pool', 'aggregate'],
      content: (
        <>
          <p>
            The Copa do Brasil is one of the most democratic competitions on the
            calendar: it starts with around 92 teams from every tier (1, 2, 3 and
            4) and ends with just one champion, after a brutal series of knockout
            ties. The prize money is generous, the tradition is old (it has
            existed since 1989) and the chance of an upset is sky-high. All of
            this makes it one of the most exciting — and also one of the hardest
            to predict.
          </p>
          <p>
            We will explain how the tournament works and, more importantly, how
            it affects your pool strategy.
          </p>

          <h2>The knockout format (pure knockout)</h2>
          <p>
            Unlike the Brazilian league, the Copa do Brasil has <strong>no
            round-robin</strong>. Everything is knockout. Lost? You're out. There
            is no second chance.
          </p>
          <p>
            The Copa do Brasil is divided into the following stages (typical
            format — occasional variations may occur depending on the annual
            regulations):
          </p>
          <ul>
            <li><strong>1st and 2nd stages:</strong> single match, at the home of the lower-ranked team</li>
            <li><strong>3rd stage:</strong> two legs</li>
            <li><strong>Round of 16, quarter-finals and semi-finals:</strong> two legs</li>
            <li><strong>Final:</strong> two legs</li>
          </ul>
          <p>
            In the first two stages, a single match means <strong>the home team
            only needs to win</strong> — a draw in 90 minutes eliminates the home
            side. These stages mainly feature tier-3 and tier-4 teams, with few
            elite stars on the pitch. The surprises start here.
          </p>

          <h2>When does each team enter?</h2>
          <p>
            The Copa do Brasil has staggered entries to make sure the big teams
            don't knock out the small teams right away:
          </p>
          <ul>
            <li>
              Tier-3 and tier-4 teams and smaller state-champion clubs enter in
              the 1st and 2nd stages.
            </li>
            <li>
              Tier-2 teams enter from the 3rd stage onwards.
            </li>
            <li>
              <strong>Série A (tier-1) teams enter only in the round of 16</strong>{' '}
              (with a few exceptions, such as recently relegated teams).
            </li>
          </ul>
          <p>
            This means that, for a Série A team, the Copa do Brasil &quot;really
            starts&quot; in the round of 16. But for every fan of a small team,
            it is a golden chance: each win against a bigger team is worth
            millions in prize money and prestige.
          </p>

          <h2>Prize money: why every team takes it seriously</h2>
          <p>
            The Copa do Brasil is, by far, the competition with the best
            proportional prize money in the country. Each stage eliminates clubs'
            chance of receiving additional sums, which combined can exceed R$ 80
            million for the champion. For clubs from the interior or from tier 3,
            advancing 2 or 3 stages can equal a full annual budget.
          </p>
          <p>
            What that means for the pool: <strong>every team plays for
            real</strong>. There is no &quot;reserve team to rest for something
            else&quot; — for small clubs, the Copa do Brasil IS the main
            competition.
          </p>

          <h2>Why the Copa do Brasil is so hard to predict</h2>
          <p>
            Three main reasons:
          </p>

          <h3>1. Uneven knowledge of the entrants</h3>
          <p>
            You probably know the line-ups of Flamengo, Palmeiras and
            Atlético-MG. But what about Boavista-RJ? Or ABC? Or Sousa-PB?{' '}
            <strong>Without information, your predictions on early-stage matches
            are guesses</strong>. Anyone who follows regional football or
            tier-3/tier-4 has a real advantage here.
          </p>

          <h3>2. A single match favours upsets</h3>
          <p>
            In a single-match knockout, one team only has to make one mistake and
            the underdog takes it. Over two legs, the better team has 180 minutes
            to assert itself; in a single match, there are only 90.
            Statistically, <strong>a single Copa do Brasil match has roughly 30%
            more upsets than a two-legged tie</strong>.
          </p>

          <h3>3. A tiebreaker that confuses many people</h3>
          <p>
            Over two legs, the tiebreaker is the aggregate (the sum of the scores
            of the two matches). If the aggregate is level, it goes to extra time
            and, if the draw persists, to penalties. <strong>There is no longer
            an &quot;away goal counts double&quot; rule</strong> in the Copa do
            Brasil since 2022. Anyone who predicts thinking about the old rule
            gets a lot wrong.
          </p>

          <h2>Pool strategy in the Copa do Brasil</h2>
          <p>
            Considering these characteristics, three recommendations:
          </p>

          <h3>1. Be conservative in single-match early stages</h3>
          <p>
            When two very unequal teams meet (tier 1 vs tier 3), predict a win
            for the favourite, but avoid thrashings. <strong>In the early stages,
            matches tend to be scrappy</strong> — the big team doesn't set a full
            tempo because its mind is on other competitions, and the small one
            sits back. 1-0 and 2-0 are far more common scores than 4-0 or 5-0 in
            these stages.
          </p>

          <h3>2. Bet on the second leg to get the aggregate right</h3>
          <p>
            Over two legs, the second leg tends to be more decisive. If the first
            match was 0-0 or 1-1, expect a more open match in the second one —
            the home team needs to open the scoring to gain an advantage.{' '}
            <strong>Second legs are more thrilling and have higher
            scores</strong>.
          </p>

          <h3>3. Mind the teams' internal calendar</h3>
          <p>
            Série A teams that play the Copa Libertadores tend to prioritise the
            continental tournament. If a big club has a Libertadores match 3 days
            later, it is likely to field a reserve team in the Copa do Brasil.
            Then it pays to bet on an upset — but carefully, because that
            &quot;choice&quot; does not always happen.
          </p>

          <h2>How to create a Copa do Brasil pool at Bolão Elite</h2>
          <p>
            The Copa do Brasil has very asymmetric stages — some with few
            matches, others with several. At Bolão Elite, the{' '}
            <a href="/en/copa-do-brasil-pool">Copa do Brasil pool</a> page
            already comes with the stage structure configured. You create the
            pool, choose the scoring and the system imports the matches
            automatically.
          </p>
          <p>
            We recommend Rule C (10/5/2) — an exact score is worth 10 pts,
            winner/draw 5 pts, correct goals for one of the teams 2 pts. Since
            the Copa do Brasil has many scrappy matches, the granularity helps
            keep everyone in the running until the end. Those who prefer
            simplicity can use Rule B (3/1).
          </p>

          <h2>Conclusion</h2>
          <p>
            The Copa do Brasil is a controlled lottery: it has clear favourites
            (the Série A teams), but the path to the final goes through many
            matches where the upset has real force. To run a pool of it, prepare
            yourself to get more wrong than usual — and enjoy that. The surprises
            are what make this competition unique on the Brazilian calendar.
          </p>
          <p>
            For more content,{' '}
            <a href="/en/blog">see all blog articles</a>. To understand terms
            like &quot;aggregate&quot; and &quot;knockout&quot;, take a look at
            the <a href="/en/glossary">glossary</a>.
          </p>
        </>
      ),
    },
    es: {
      slug: 'copa-de-brasil-2026',
      title: 'Copa de Brasil 2026: cómo funciona la eliminatoria y su impacto en la quiniela',
      excerpt:
        'Un torneo con 92 participantes, partidos eliminatorios y premios millonarios — entiende el formato de la Copa de Brasil y por qué exige una estrategia de pronósticos diferente.',
      readTimeMinutes: 7,
      coverImageAlt: 'Portada del artículo: Copa de Brasil 2026 — la eliminatoria explicada',
      coverImage: '/blog/copa-do-brasil-2026-es.svg',
      keywords: ['copa de brasil 2026', 'eliminatoria', 'eliminatorias', 'quiniela copa de brasil', 'global'],
      content: (
        <>
          <p>
            La Copa de Brasil es una de las competiciones más democráticas del
            calendario: comienza con cerca de 92 equipos de todas las Series (A,
            B, C y D) y termina con un solo campeón, tras una serie brutal de
            eliminatorias. El premio es generoso, la tradición es antigua (existe
            desde 1989) y la posibilidad de sorpresa es altísima. Todo esto la
            convierte en una de las más emocionantes — y también de las más
            difíciles de pronosticar.
          </p>
          <p>
            Vamos a explicar cómo funciona el torneo y, más importante, cómo
            afecta a tu estrategia en la quiniela.
          </p>

          <h2>El formato eliminatorio (eliminatoria pura)</h2>
          <p>
            Al contrario que el Brasileirão, en la Copa de Brasil <strong>no
            existe el todos contra todos</strong>. Todo es eliminatorio.
            ¿Perdiste? Fuera. No hay segunda oportunidad.
          </p>
          <p>
            La Copa de Brasil se divide en las siguientes fases (formato típico —
            pueden darse variaciones puntuales según el reglamento anual):
          </p>
          <ul>
            <li><strong>1.ª y 2.ª fases:</strong> partido único, en casa del equipo peor clasificado</li>
            <li><strong>3.ª fase:</strong> partido de ida y vuelta</li>
            <li><strong>Octavos, cuartos y semifinales:</strong> ida y vuelta</li>
            <li><strong>Final:</strong> ida y vuelta</li>
          </ul>
          <p>
            En las dos primeras fases, el partido único significa que <strong>quien
            juega en casa solo necesita ganar</strong> — un empate en 90 minutos
            elimina al local. En estas fases entran principalmente equipos de la
            Serie C y D, con pocas estrellas de élite en el campo. Las sorpresas
            empiezan aquí.
          </p>

          <h2>¿Cuándo entra cada equipo?</h2>
          <p>
            La Copa de Brasil tiene entradas escalonadas para garantizar que los
            equipos grandes no eliminen a los pequeños de entrada:
          </p>
          <ul>
            <li>
              Los equipos de Serie C, D y los clubes campeones estatales menores
              entran en las 1.ª y 2.ª fases.
            </li>
            <li>
              Los equipos de Serie B entran a partir de la 3.ª fase.
            </li>
            <li>
              <strong>Los equipos de Serie A entran solo en los octavos de
              final</strong> (con pocas excepciones, como los equipos descendidos
              recientemente).
            </li>
          </ul>
          <p>
            Esto significa que, para un equipo de Serie A, la Copa de Brasil
            &quot;empieza de verdad&quot; en octavos. Pero para todo aficionado de
            un equipo pequeño, es una oportunidad de oro: cada victoria contra un
            equipo mayor vale millones en premios y prestigio.
          </p>

          <h2>Los premios: por qué todo equipo se lo toma en serio</h2>
          <p>
            La Copa de Brasil es, de lejos, la competición con el mejor premio
            proporcional del país. Cada fase elimina para los clubes la
            posibilidad de recibir sumas adicionales, que sumadas pueden superar
            los R$ 80 millones para el campeón. Para clubes del interior o de la
            Serie C, avanzar 2 o 3 fases puede equivaler al presupuesto anual
            completo.
          </p>
          <p>
            Traducido a la quiniela: <strong>todos los equipos juegan en
            serio</strong>. No hay &quot;equipo suplente para reservarse para otra
            cosa&quot; — para los clubes pequeños, la Copa de Brasil ES la
            competición principal.
          </p>

          <h2>Por qué la Copa de Brasil es tan difícil de pronosticar</h2>
          <p>
            Tres razones principales:
          </p>

          <h3>1. Conocimiento desigual entre los participantes</h3>
          <p>
            Probablemente sepas la alineación del Flamengo, el Palmeiras y el
            Atlético-MG. Pero ¿y el Boavista-RJ? ¿Y el ABC? ¿Y el Sousa-PB?{' '}
            <strong>Sin información, tus pronósticos en partidos de las fases
            iniciales son un tiro al aire</strong>. Quien sigue el fútbol regional
            o la Serie C/D tiene una ventaja real aquí.
          </p>

          <h3>2. El partido único favorece la sorpresa</h3>
          <p>
            En una eliminatoria a partido único, basta con que un equipo falle
            una jugada y el desfavorito se lleva el pase. A ida y vuelta, el mejor
            equipo tiene 180 minutos para imponerse; a partido único, son solo 90.
            Estadísticamente, <strong>un partido único de la Copa de Brasil tiene
            cerca de un 30% más de sorpresas que la ida y vuelta</strong>.
          </p>

          <h3>3. Un criterio de desempate confuso para mucha gente</h3>
          <p>
            A ida y vuelta, el criterio de desempate es el global (la suma de los
            marcadores de los dos partidos). Si el global es igual, va a la
            prórroga y, si persiste el empate, a los penales. <strong>Ya no existe
            el &quot;gol fuera de casa vale doble&quot;</strong> en la Copa de
            Brasil desde 2022. Quien pronostica pensando en la regla antigua falla
            mucho.
          </p>

          <h2>Estrategia de quiniela en la Copa de Brasil</h2>
          <p>
            Teniendo en cuenta estas características, tres recomendaciones:
          </p>

          <h3>1. Sé conservador en las fases iniciales a partido único</h3>
          <p>
            Cuando se enfrentan dos equipos muy desiguales (Serie A vs Serie C),
            pronostica la victoria del favorito, pero evita las goleadas.{' '}
            <strong>En las primeras fases, los partidos suelen ser
            trabados</strong> — el equipo grande no impone un ritmo total porque
            tiene la cabeza en otras competiciones, y el pequeño se encierra. El
            1-0 y el 2-0 son marcadores mucho más comunes que el 4-0 o el 5-0 en
            estas fases.
          </p>

          <h3>2. Apuesta por el partido de vuelta para acertar el global</h3>
          <p>
            A ida y vuelta, el partido de vuelta suele ser más decisivo. Si el
            primer partido fue 0-0 o 1-1, espera un partido más abierto en el
            segundo — el equipo local necesita abrir el marcador para tener
            ventaja. <strong>Los partidos de vuelta entusiasman más y tienen
            marcadores más altos</strong>.
          </p>

          <h3>3. Atención al calendario interno de los equipos</h3>
          <p>
            Los equipos de Serie A que disputan la Libertadores suelen priorizar
            el torneo continental. Si un club grande tiene partido de
            Libertadores 3 días después, es probable que alinee un equipo
            suplente en la Copa de Brasil. Ahí conviene apostar por la sorpresa —
            pero con cuidado, porque esa &quot;elección&quot; no siempre ocurre.
          </p>

          <h2>Cómo crear una quiniela de la Copa de Brasil en Bolão Elite</h2>
          <p>
            La Copa de Brasil tiene fases muy asimétricas — algunas con pocos
            partidos, otras con varios. En Bolão Elite, la página{' '}
            <a href="/es/quiniela-copa-de-brasil">quiniela de la Copa de
            Brasil</a> ya viene con la estructura de fases configurada. Creas la
            quiniela, eliges la puntuación y el sistema importa los partidos
            automáticamente.
          </p>
          <p>
            Recomendamos la Regla C (10/5/2) — el marcador exacto vale 10 pts,
            ganador/empate 5 pts, goles de uno de los equipos correctos 2 pts.
            Como la Copa de Brasil tiene muchos partidos trabados, la
            granularidad ayuda a mantener a todo el mundo en la pelea hasta el
            final. Quien prefiere la simplicidad puede usar la Regla B (3/1).
          </p>

          <h2>Conclusión</h2>
          <p>
            La Copa de Brasil es una lotería controlada: tiene favoritos claros
            (los equipos de Serie A), pero el camino hasta la final pasa por
            muchos partidos donde la sorpresa tiene fuerza. Para hacer una
            quiniela de ella, prepárate para fallar más de lo habitual — y
            diviértete con ello. Las sorpresas son lo que hace que esta
            competición sea única en el calendario brasileño.
          </p>
          <p>
            Para más contenido,{' '}
            <a href="/es/blog">mira todos los artículos del blog</a>. Para
            entender términos como &quot;global&quot; y &quot;eliminatoria&quot;,
            echa un vistazo al <a href="/es/glosario">glosario</a>.
          </p>
        </>
      ),
    },
  },
};
